import { useDialog } from "_shared/_providers/dialogs/dialog";
import EntityMember from "_shared/entity-members/entity-member";
import { FeeScheduleTemplateCategory } from "_shared/fee-schedules/categories/fee-schedule-template-category";
import ReconcileFeeScheduleStaffingDialog from "_shared/fee-schedules/categories/_views/reconcile-fee-schedule-staffing-dialog";
import {
  FeeScheduleStaffingDecision,
  staffingNotOnTeam,
  withStaffingDecisionsApplied,
} from "_shared/fee-schedules/categories/fee-schedule-template-staffing";
import { createElement } from "react";

/** What the user chose to do about people a template names who are not on the proposal's team. */
export type FeeScheduleStaffingOutcome<T extends FeeScheduleTemplateCategory> =
  | { applied: false }
  | { applied: true; categories: T[]; membersToAddToTeam: readonly EntityMember[] };

export type ReconcileFeeScheduleStaffing = <T extends FeeScheduleTemplateCategory>(params: {
  categories: readonly T[];
  teamMembers: readonly EntityMember[];
  /**
   * Whether the proposal's vendor team is masked to this viewer. Not inferable from an empty
   * `teamMembers`, which is also what a real proposal with no team yet looks like.
   */
  teamIsMasked?: boolean;
}) => Promise<FeeScheduleStaffingOutcome<T>>;

/**
 * Asks what to do about people a fee schedule template names who are not on this proposal's team.
 *
 * The template can name anyone its author would put on a team; the proposal may only bill somebody
 * on its own vendor team, which is what `WorkAgreement.ValidateFeeSchedule` enforces before a hire.
 * #5237 requires that the difference is put to the user rather than resolved behind their back,
 * either way round: silently adding people to a negotiated team and silently throwing away the
 * staffing a template exists to carry are both wrong.
 *
 * The question is asked per person — add them, hand the rate to somebody the team already has, or
 * leave it unassigned — because a template that names three people is rarely three of the same
 * case. `ReconcileFeeScheduleStaffingDialog` is the dialog; this hook is the promise around it.
 *
 * A template whose people are all on the team already asks nothing, which is the ordinary case.
 */
export default function useReconcileFeeScheduleStaffing(): ReconcileFeeScheduleStaffing {
  const { openDialog, popDialog } = useDialog();

  return async function reconcile<T extends FeeScheduleTemplateCategory>(params: {
    categories: readonly T[];
    teamMembers: readonly EntityMember[];
    teamIsMasked?: boolean;
  }): Promise<FeeScheduleStaffingOutcome<T>> {
    const { categories, teamMembers, teamIsMasked } = params;
    const strangers = staffingNotOnTeam(categories, teamMembers);
    if (strangers.length === 0) {
      return { applied: true, categories: [...categories], membersToAddToTeam: [] };
    }

    const decisions = await new Promise<ReadonlyMap<string, FeeScheduleStaffingDecision> | null>(
      (resolve) => {
        let settled = false;
        // Every way out has to answer, including the dialog's own close button: a caller is
        // awaiting this, and a dismissal that resolved nothing would leave the template half
        // applied with no way back.
        const settle = (answer: ReadonlyMap<string, FeeScheduleStaffingDecision> | null) => {
          if (settled) return;
          settled = true;
          resolve(answer);
        };
        openDialog({
          title:
            strangers.length === 1
              ? "This rate is for someone not on the team"
              : "These rates are for people not on the team",
          component: createElement(ReconcileFeeScheduleStaffingDialog, {
            strangers,
            categories,
            teamMembers,
            teamIsMasked,
            onDecided: (answer) => {
              settle(answer);
              popDialog();
            },
          }),
          MuiProps: { fullWidth: true, maxWidth: "sm" },
          onClose: () => settle(null),
        });
      },
    );

    if (!decisions) return { applied: false };
    return { applied: true, ...withStaffingDecisionsApplied(categories, teamMembers, decisions) };
  };
}
